import type { TemplateItem, TemplateSection } from 'inspection-types';

import {
  deriveReorderCapability,
  deriveSectionCapabilities,
} from '../model/capabilities';
import { countSectionItems } from '../model/counts';
import type { TemplatePermissions } from '../model/operations';
import { isInherited } from '../model/origin';
import { sortItems } from '../model/templateProjection';
import type { CustomizationState } from '../state/customizationState';
import {
  failureFor,
  pendingOpFor,
  pendingReorderChanges,
} from '../state/selectors';
import { createMenuEntries, type MenuPresentation } from './createMenuEntries';
import {
  createQuestionRowViewModel,
  type QuestionOperations,
  type QuestionRowViewModel,
} from './createQuestionRowViewModel';
import { createReorderViewModel } from './createReorderViewModel';
import type {
  ReorderViewModel,
  RowFailureViewModel,
  RowSpinnerViewModel,
  SectionActionId,
  SectionMenuEntry,
} from './rowTypes';
import { memoizeByNode } from './memoizeByNode';
import { LABEL_KEYS, type BadgeViewModel, type Message } from './messages';
import { createFailure, createSpinner } from './operationPresentation';

export type SectionViewModel = {
  id: string;
  name: string;
  isHidden: boolean;
  isExpanded: boolean;
  badges: BadgeViewModel[];
  counts: Message | null;
  menuLabel: Message;
  menuEntries: SectionMenuEntry[];
  reorder: ReorderViewModel;
  spinner: RowSpinnerViewModel | null;
  failure: RowFailureViewModel | null;
  rows: QuestionRowViewModel[];
  emptyMessage: Message | null;
};

export type OperationInputs = Pick<
  CustomizationState,
  'pendingOps' | 'failures' | 'staleReason'
> & {
  permissions: TemplatePermissions;
};

const NO_PENDING_OPS: OperationInputs['pendingOps'] = {};

const createMemoizedRow = memoizeByNode(createQuestionRowViewModel);

// A menu entry is only offered when capabilities permits the user and the operation, so
// NOT_PERMITTED is never shown. MUTATION_IN_FLIGHT only blocks a reorder.
// They stay in the map to keep it exhaustive.
const MENU_PRESENTATION: MenuPresentation<SectionActionId> = {
  labelKeys: {
    hide: LABEL_KEYS.hideSectionMenuEntry,
    unhide: LABEL_KEYS.unhideSectionMenuEntry,
  },
  blockedMessages: {
    SAME_TARGET_IN_FLIGHT: { key: LABEL_KEYS.blockedSectionInFlightTooltip },
    REORDER_IN_FLIGHT: { key: LABEL_KEYS.blockedReorderInFlightTooltip },
    MUTATION_IN_FLIGHT: null,
    REPLICA_STALE: { key: LABEL_KEYS.blockedStaleTooltip },
    NOT_PERMITTED: null,
  },
};

function createCounts(section: TemplateSection): Message | null {
  const { visible, total, local } = countSectionItems(section);

  if (isInherited(section)) {
    return local > 0
      ? {
          key: LABEL_KEYS.sectionCountsInherited,
          params: { visible, total, local, count: local },
        }
      : {
          key: LABEL_KEYS.sectionCountsTemplateOnly,
          params: { visible, total },
        };
  }

  return local > 0
    ? { key: LABEL_KEYS.sectionCountsLocal, params: { count: local } }
    : null;
}

function createMenu(
  section: TemplateSection,
  operations: OperationInputs
): SectionMenuEntry[] {
  const { pendingOps, staleReason, permissions } = operations;
  const capabilities = deriveSectionCapabilities(section, {
    pendingOps,
    staleReason,
    permissions,
  });

  return createMenuEntries(capabilities, MENU_PRESENTATION);
}

function createReorder(
  section: TemplateSection,
  operations: OperationInputs
): ReorderViewModel {
  const handleLabel = {
    key: LABEL_KEYS.reorderSectionHandleLabel,
    params: { section: section.name },
  };

  return createReorderViewModel(
    deriveReorderCapability(operations),
    handleLabel
  );
}

function isReorderAllowed({ permissions }: OperationInputs) {
  return permissions.canReorder;
}

function hasPendingReorder({ pendingOps }: OperationInputs) {
  return pendingReorderChanges({ pendingOps }).length > 0;
}

function createQuestionOperations(
  questionId: string,
  operations: OperationInputs
): QuestionOperations {
  const { staleReason, permissions } = operations;
  const pendingOp = pendingOpFor(operations, questionId);
  const failure = failureFor(operations, questionId);
  const pendingOps =
    isReorderAllowed(operations) || hasPendingReorder(operations)
      ? operations.pendingOps
      : NO_PENDING_OPS;

  return {
    pendingOp,
    pendingOps,
    failure,
    staleReason,
    permissions,
  };
}

function createRows(
  items: TemplateItem[],
  isSectionHidden: boolean,
  operations: OperationInputs
): QuestionRowViewModel[] {
  const sortedItems = sortItems(items);

  return sortedItems.map(item => {
    const questionOperations = createQuestionOperations(item.id, operations);

    return createMemoizedRow(item, isSectionHidden, questionOperations);
  });
}

export function createSectionViewModel(
  section: TemplateSection,
  isExpanded: boolean,
  operations: OperationInputs
): SectionViewModel {
  const isHidden = Boolean(section.removed);
  const rows = isExpanded
    ? createRows(section.items, isHidden, operations)
    : [];
  const isEmpty = isExpanded && section.items.length === 0;
  const menuEntries = createMenu(section, operations);
  const pendingOp = pendingOpFor(operations, section.id);
  const spinner = createSpinner(pendingOp, section.name);
  const failedOperation = failureFor(operations, section.id);
  const failure = createFailure(failedOperation, section.name);

  return {
    id: section.id,
    name: section.name,
    isHidden,
    isExpanded,
    badges: isInherited(section)
      ? [{ kind: 'lock', tooltipKey: LABEL_KEYS.lockSectionTooltip }]
      : [],
    counts: createCounts(section),
    menuLabel: {
      key: LABEL_KEYS.sectionMenuButtonLabel,
      params: { section: section.name },
    },
    menuEntries,
    reorder: createReorder(section, operations),
    spinner,
    failure,
    rows,
    emptyMessage: isEmpty ? { key: LABEL_KEYS.noQuestions } : null,
  };
}
